import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react";
import { CheckIcon, SelectorIcon } from "@heroicons/react/solid";
import { useTranslation } from "react-i18next";
import PeriodClosePanel from "../../Components/v2/Accounting/PeriodClosePanel";
import BusinessStatusBanner from "../../Components/v2/Business/BusinessStatusBanner";
import EmptyBusiness from "../../Components/EmptyBusiness";
import { classNames } from "../../Utils";
import {
  requestListBusiness,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";

/**
 * v1 Tutup Buku. Renders the same panel as the v2 route — the panel owns no page
 * chrome, so here it sits in the plain v1 card and takes its heading from App.js
 * via the route title.
 *
 * v2 always closes the books of `user.mainBusiness`; v1 lets the user pick, the
 * same way Pages/OpeningBalance does, because the books are per-business and v1
 * has no sidebar pinning the active one. Reached from the Account page with
 * `?id=` for the business being managed.
 */
const PeriodClose = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthState();
  const { businesses, loading } = useBusinessTransactionState();
  const dispatch = useBusinessTransactionDispatch();

  // `loading` starts false, so without this the empty-state would flash for one
  // frame before the effect below fires.
  const [requested, setRequested] = useState(false);

  // Only the id is state; the business object is derived. Keeping the selection
  // as a single id avoids the sync-effect that would otherwise have to reconcile
  // a stored object against every refetch of the list.
  const [selectedID, setSelectedID] = useState(() => searchParams.get("id") || "");

  useEffect(() => {
    if (!user?.id) return;
    setRequested(true);
    requestListBusiness(dispatch, { owner: user.id, page: 1, limit: 10 });
  }, [dispatch, user?.id]);

  const businessID = selectedID || user?.mainBusiness?.id || businesses[0]?.id;
  // mainBusiness is checked separately because the list is capped at 10 entries
  // and the active business is not guaranteed to be among them.
  const business =
    (businesses || []).find((item) => item.id === businessID) ||
    (user?.mainBusiness?.id === businessID ? user.mainBusiness : null);

  const onSelect = (value) => {
    setSelectedID(value.id);
    // Keep the URL truthful so a refresh or a shared link lands on the same
    // business. Replace, so switching does not stack up history entries.
    setSearchParams({ id: value.id }, { replace: true });
  };

  if (businesses.length === 0) {
    return <EmptyBusiness loading={loading || !requested} />;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Same Listbox as Pages/OpeningBalance — one business picker shape across
          v1 accounting. `relative` anchors the dropdown to the button. */}
      <div className="relative w-full sm:w-48">
        {/* `by="id"` because the selected business can come from
            user.mainBusiness, which is a different object than the matching
            list entry — identity comparison would leave nothing ticked. */}
        <Listbox value={business || {}} by="id" onChange={onSelect}>
          {({ open }) => (
            <>
              <ListboxButton className="flex items-center justify-between w-full py-2 px-3 border border-gray-300 rounded-md bg-white shadow-sm text-sm">
                <span className="max-w-full ml-3 mr-2 text-sm text-ellipsis overflow-hidden sm:whitespace-nowrap">
                  {business?.name || business?.label || t("Business Name")}
                </span>
                <SelectorIcon className="w-5 h-5 text-gray-500" />
              </ListboxButton>
              <Transition
                show={open}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions className="absolute z-10 mt-1 bg-white shadow-lg max-h-56 w-full rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none text-sm">
                  {businesses.map((item) => (
                    <ListboxOption
                      key={item.value}
                      className={({ focus }) =>
                        classNames(
                          focus ? "text-white bg-blue-600" : "text-gray-900",
                          "cursor-default select-none relative py-2 pl-3 pr-9"
                        )
                      }
                      value={item}
                    >
                      {({ selected, focus }) => (
                        <div className="flex items-center justify-between">
                          <span
                            className={classNames(
                              selected ? "font-semibold" : "font-normal",
                              "ml-3 mr-2 block truncate"
                            )}
                          >
                            {item.label}
                          </span>
                          {selected ? (
                            <span
                              className={classNames(
                                focus ? "text-white" : "text-blue-600",
                                "absolute inset-y-0 right-0 flex items-center pr-4"
                              )}
                            >
                              <CheckIcon className="h-5 w-5" />
                            </span>
                          ) : null}
                        </div>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </>
          )}
        </Listbox>
      </div>

      {/* Why the lifecycle strip is here and not only inside the restricted
          modal: the modal explains the block *after* a click, this states it
          before one. Renders null for a healthy business, and follows the picker
          because it describes whichever business is selected. v2 gets the same
          strip from AccountingWrapper. `onRecord` is omitted deliberately —
          recording a transaction is not this page's job, so the inactivity
          states show their message without a CTA that leads away. */}
      {business?.id && <BusinessStatusBanner business={business} />}

      {/* Concentric radii: the panel's inner sections are rounded-lg, so the
          card that holds them is rounded-xl + this padding. */}
      <div className="bg-white border border-gray-200 shadow rounded-md sm:rounded-xl p-4 sm:p-6">
        <PeriodClosePanel
          // Remounts on switch so the panel's local form state (chosen candidate,
          // reason, open confirmations) never carries over to another business.
          key={businessID}
          businessID={businessID}
          business={business}
        />
      </div>
    </div>
  );
};

export default PeriodClose;
